import {
	Breadcrumb,
	getTheme,
	IBreadcrumbItem,
	Icon,
	mergeStyleSets,
	Pivot,
	PivotItem,
	PrimaryButton,
	Stack,
	Text,
} from '@fluentui/react';
import * as React from 'react';
import { navigateTo } from '../../../src/hooks';
import { MsCertifiedBadge } from '../../MsCertifiedBadge';
import { Question } from '../../Question';

const PRODUCT_URL = 'https://reenhanced.com/products/power-automate-for-woocommerce/';
const LOGO_URL = 'https://reenhanced.com/wp-content/uploads/2022/03/logo.jpg';
const SCREENSHOT_TRIGGER = 'https://reenhanced.com/wp-content/uploads/2022/03/Screenshot-2022-05-10-110626.jpg';
const SCREENSHOT_EVENTS = 'https://reenhanced.com/wp-content/uploads/2022/03/Screenshot-2022-05-10-111015.jpg';

const theme = getTheme();

const style = mergeStyleSets({
	container: {},

	heroBanner: {
		background: `linear-gradient(135deg, ${theme.palette.themePrimary} 0%, ${theme.palette.themeDarker} 100%)`,
		borderRadius: 12,
		padding: '32px 36px',
		marginBottom: 28,
		color: '#fff',
	},
	heroLogoRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 16,
		marginBottom: 12,
	},
	heroLogo: {
		width: 56,
		height: 56,
		borderRadius: 10,
		objectFit: 'cover' as const,
		flexShrink: 0,
	},
	heroTitle: {
		...theme.fonts.xxLarge,
		color: '#fff',
		margin: 0,
		fontWeight: 700,
	},
	heroSubtitle: {
		...theme.fonts.mediumPlus,
		color: 'rgba(255,255,255,0.88)',
		marginBottom: 20,
		lineHeight: '1.6',
	},
	heroPricing: {
		...theme.fonts.small,
		color: 'rgba(255,255,255,0.7)',
		marginTop: 8,
		display: 'block' as const,
	},

	trustRow: {
		display: 'flex',
		flexWrap: 'wrap' as const,
		gap: 10,
		marginBottom: 28,
	},
	trustPill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		backgroundColor: theme.palette.neutralLighterAlt,
		border: `1px solid ${theme.palette.neutralLight}`,
		borderRadius: 20,
		padding: '5px 12px',
		fontSize: 13,
		color: theme.palette.neutralPrimary,
	},

	sectionTitle: {
		...theme.fonts.xLarge,
		fontWeight: 700,
		marginBottom: 16,
		marginTop: 24,
	},

	featureGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
		gap: 14,
		marginBottom: 24,
	},
	featureCard: {
		backgroundColor: theme.palette.neutralLighterAlt,
		border: `1px solid ${theme.palette.neutralLight}`,
		borderRadius: 8,
		padding: '14px 16px',
	},
	featureIcon: {
		color: theme.palette.themePrimary,
		fontSize: 20,
		marginBottom: 6,
		display: 'block' as const,
	},
	featureTitle: {
		...theme.fonts.medium,
		fontWeight: 700,
		marginBottom: 4,
	},
	featureText: {
		...theme.fonts.small,
		color: theme.palette.neutralSecondary,
		lineHeight: '1.5',
	},

	screenshot: {
		width: '100%',
		maxWidth: 560,
		borderRadius: 8,
		border: `1px solid ${theme.palette.neutralLight}`,
		display: 'block' as const,
		margin: '16px 0',
	},

	useCaseList: {
		listStyle: 'none',
		paddingLeft: 0,
		margin: 0,
	},
	useCaseItem: {
		display: 'flex',
		gap: 12,
		padding: '12px 0',
		borderBottom: `1px solid ${theme.palette.neutralLighter}`,
	},
	useCaseIcon: {
		color: theme.palette.themePrimary,
		fontSize: 20,
		flexShrink: 0,
		marginTop: 2,
	},
	useCaseTitle: {
		...theme.fonts.medium,
		fontWeight: 700,
		marginBottom: 2,
	},
	useCaseDesc: {
		...theme.fonts.small,
		color: theme.palette.neutralSecondary,
	},

	triggerGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
		gap: 8,
		marginBottom: 16,
	},
	triggerItem: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		padding: '6px 10px',
		backgroundColor: theme.palette.neutralLighterAlt,
		borderRadius: 6,
		fontSize: 13,
	},

	resourcePill: {
		display: 'inline-block',
		backgroundColor: theme.palette.themeLight,
		color: theme.palette.themeDark,
		borderRadius: 14,
		padding: '3px 12px',
		fontSize: 13,
		fontWeight: 600,
		margin: '4px',
	},

	highlight: {
		backgroundColor: 'rgba(255, 245, 100, 0.65)',
		padding: '0 4px',
		borderRadius: 2,
	},
});

const triggers = [
	'Order Created', 'Order Updated', 'Order Paid', 'Order Status Changed', 'Order Deleted', 'Order Restored',
	'Product Created', 'Product Updated', 'Product Deleted', 'Product Restored',
	'Customer Created', 'Customer Updated', 'Customer Deleted',
	'Coupon Created', 'Coupon Updated', 'Coupon Deleted', 'Coupon Restored',
	'Subscription Created', 'Subscription Updated', 'Subscription Deleted',
	'Subscription Status Changed', 'Subscription Renewed', 'Subscription Renewal Failed', 'Subscription Switched',
	'Order Note Added',
];

const resources = ['Coupons', 'Customers & Roles', 'Orders', 'Order Notes', 'Products', 'Product Variations', 'Product Attributes & Terms', 'Subscriptions'];

const useCases = [
	{
		icon: 'ContactCard',
		title: 'Sync new customers to your CRM',
		desc: 'Automatically create customers in Dynamics 365 or Salesforce the moment they register in WooCommerce — no manual data entry.',
	},
	{
		icon: 'TeamsLogo',
		title: 'Real-time Teams alerts on high-value orders',
		desc: 'Post a message to a Microsoft Teams channel when a large order is placed or a subscription renewal fails.',
	},
	{
		icon: 'ExcelLogo',
		title: 'Live order export to Excel or SharePoint',
		desc: 'Add every new order to a SharePoint list or Excel spreadsheet that your fulfillment team works from every day.',
	},
	{
		icon: 'ProductVariant',
		title: 'Create products from Dynamics 365',
		desc: 'Push new products and variations from your ERP directly into your WooCommerce catalog without touching the dashboard.',
	},
	{
		icon: 'Workflow',
		title: 'Approval workflow for order refunds',
		desc: 'Route refund requests through a Microsoft Approvals flow so the right people are notified before any action is taken.',
	},
	{
		icon: 'CheckList',
		title: 'Subscription status lookups in Power Apps',
		desc: 'Build a Power App that checks WooCommerce subscription status in real time before triggering any downstream automation.',
	},
	{
		icon: 'ProductList',
		title: 'Inventory sync with your ERP',
		desc: 'Update WooCommerce stock levels automatically when inventory changes in Business Central, NAV, or any other Microsoft system.',
	},
	{
		icon: 'Mail',
		title: 'Automated marketing campaign triggers',
		desc: 'Kick off Dynamics 365 customer journeys or email campaigns the instant an order is placed or a coupon is redeemed.',
	},
];

export const Woopower: React.FC = () => {
	const breadcrumbs: IBreadcrumbItem[] = [
		{
			text: 'Products',
			key: 'products',
			onClick: (ev) => { ev.preventDefault(); navigateTo('/products'); },
		},
		{ text: 'Power Automate for WooCommerce', key: 'woopower' },
	];

	return (
		<>
			<Breadcrumb items={breadcrumbs} />
			<div className={style.container}>

				{/* ── Hero ── */}
				<div className={style.heroBanner}>
					<div className={style.heroLogoRow}>
						<img src={LOGO_URL} alt="Power Automate for WooCommerce" className={style.heroLogo} />
						<h1 className={style.heroTitle}>Power Automate for WooCommerce</h1>
					</div>
					<p className={style.heroSubtitle}>
						Unlock the full potential of your WooCommerce store by connecting it to Microsoft Power Automate
						and over <strong style={{ color: '#fff' }}>1,000+ services</strong> — including Dynamics 365,
						Teams, Excel, SharePoint, and Outlook. Stop manually copying data between systems and let
						Power Automate handle the heavy lifting.
					</p>
					<Stack horizontal verticalAlign="center" tokens={{ childrenGap: 14 }}>
						<PrimaryButton
							text="Get Started at reenhanced.com"
							iconProps={{ iconName: 'ShoppingCart' }}
							onClick={() => { window.location.href = PRODUCT_URL; }}
							styles={{ root: { backgroundColor: '#fff', color: theme.palette.themePrimary, borderColor: '#fff' } }}
						/>
						<MsCertifiedBadge style={{ filter: 'brightness(2)' }} />
					</Stack>
					<span className={style.heroPricing}>$299 / year &nbsp;&bull;&nbsp; Microsoft Certified connector</span>
				</div>

				{/* ── Trust signals ── */}
				<div className={style.trustRow}>
					{[
						{ icon: 'Certificate', text: 'Microsoft Certified' },
						{ icon: 'Code', text: 'No Code Required' },
						{ icon: 'Lock', text: 'Enterprise-Grade Security' },
						{ icon: 'Repair', text: 'HPOS Compatible' },
						{ icon: 'Documentation', text: 'Full Audit Logging' },
						{ icon: 'DeveloperTools', text: 'Fully Extensible' },
					].map(t => (
						<span key={t.icon} className={style.trustPill}>
							<Icon iconName={t.icon} style={{ color: theme.palette.themePrimary }} />
							{t.text}
						</span>
					))}
				</div>

				<Pivot>
					{/* ── Overview ── */}
					<PivotItem headerText="Overview">
						<Text block style={{ ...(theme.fonts.mediumPlus as React.CSSProperties), lineHeight: '1.7', marginTop: 16, marginBottom: 20 }}>
							Power Automate for WooCommerce is the <span className={style.highlight}>deepest, most reliable integration</span> between
							your WooCommerce store and the Microsoft Power Platform. Built with a robust architecture featuring
							strict typing, comprehensive audit logging, and full High-Performance Order Storage (HPOS)
							compatibility — it's the last WooCommerce integration you'll ever need.
						</Text>

						<img src={SCREENSHOT_TRIGGER} alt="Trigger flows from WooCommerce events" className={style.screenshot} />

						<div className={style.sectionTitle}>Why Power Automate for WooCommerce?</div>
						<div className={style.featureGrid}>
							{[
								{
									icon: 'Savings', title: 'Leverage Existing Licensing',
									text: 'Power Automate is likely already included in your Microsoft 365 subscription — no extra cost to get started.',
								},
								{
									icon: 'Code', title: 'No Code Required',
									text: 'Build complex multi-step workflows with a simple drag-and-drop interface. Any business user can create automations.',
								},
								{
									icon: 'Shield', title: 'Enterprise-Grade Reliability',
									text: 'Strict typing, comprehensive audit logging, and HPOS support mean this plugin is built for stores that can\'t afford downtime.',
								},
								{
									icon: 'Sync', title: 'Dynamic Schema Technology',
									text: 'Triggers automatically adapt to your store\'s custom fields — new data appears in Power Automate instantly, no extra configuration needed.',
								},
								{
									icon: 'DeveloperTools', title: 'Built for Developers',
									text: 'Add custom Resources and Triggers with just a few lines of code. Every API operation and webhook delivery is fully logged.',
								},
								{
									icon: 'ProductVariant', title: 'Bidirectional Control',
									text: 'Don\'t just listen to WooCommerce — control it. Create, read, update, delete, and query store data from any flow.',
								},
							].map(f => (
								<div key={f.icon} className={style.featureCard}>
									<Icon iconName={f.icon} className={style.featureIcon} />
									<div className={style.featureTitle}>{f.title}</div>
									<div className={style.featureText}>{f.text}</div>
								</div>
							))}
						</div>

						<img src={SCREENSHOT_EVENTS} alt="Select specific WooCommerce events in Power Automate" className={style.screenshot} />
					</PivotItem>

					{/* ── Use Cases ── */}
					<PivotItem headerText="Use Cases">
						<div className={style.sectionTitle} style={{ marginTop: 16 }}>What You Can Build — Without Writing a Single Line of Code</div>
						<ul className={style.useCaseList}>
							{useCases.map((uc, i) => (
								<li key={i} className={style.useCaseItem}>
									<Icon iconName={uc.icon} className={style.useCaseIcon} />
									<div>
										<div className={style.useCaseTitle}>{uc.title}</div>
										<div className={style.useCaseDesc}>{uc.desc}</div>
									</div>
								</li>
							))}
						</ul>
					</PivotItem>

					{/* ── Triggers & Actions ── */}
					<PivotItem headerText="Triggers & Actions">
						<div className={style.sectionTitle} style={{ marginTop: 16 }}>Triggers</div>
						<Text block style={{ color: theme.palette.neutralSecondary, marginBottom: 12, fontSize: 13 }}>
							Kick off automation flows instantly when events happen in your store. All triggers support Dynamic Schema —
							your custom fields appear in Power Automate automatically.
						</Text>
						<div className={style.triggerGrid}>
							{triggers.map(t => (
								<div key={t} className={style.triggerItem}>
									<Icon iconName="LightningBolt" style={{ color: theme.palette.themePrimary, fontSize: 13, flexShrink: 0 }} />
									{t}
								</div>
							))}
						</div>

						<div className={style.sectionTitle}>Actions (Create, Read, Update, Delete, Query)</div>
						<Text block style={{ color: theme.palette.neutralSecondary, marginBottom: 12, fontSize: 13 }}>
							Every resource below supports full CRUD operations plus list/search — giving your flows complete control over your store.
						</Text>
						<div>
							{resources.map(r => (
								<span key={r} className={style.resourcePill}>{r}</span>
							))}
						</div>
					</PivotItem>

					{/* ── FAQ ── */}
					<PivotItem headerText="FAQ">
						<Question question="Do I need a separate Power Automate license?">
							<p>Power Automate is included in most Microsoft 365 business plans — check your existing licensing before purchasing anything extra. Flows run under the account that creates them; each flow can process unlimited WooCommerce events.</p>
						</Question>
						<Question question="Is Enterprise licensing available?">
							<p>Yes. Contact us directly for bulk purchases and enterprise accounts. Enterprise accounts have higher usage limits and can be configured for on-premises or private cloud deployment.</p>
						</Question>
						<Question question="What WooCommerce plugins are supported?">
							<p>Core WooCommerce and WooCommerce Subscriptions are fully supported, with full HPOS compatibility for modern WooCommerce setups.</p>
						</Question>
						<Question question="Can I extend this plugin with custom data?">
							<p>Yes — developers can add custom Resources and Triggers with just a few lines of code. The Dynamic Schema system also means any custom fields in your store automatically appear in Power Automate without any extra configuration.</p>
						</Question>
						<Question question="How is my data secured?">
							<p>All data is transmitted over SSL. Every API operation and webhook delivery is logged with full payloads so you always know exactly what happened. No sensitive data is stored in intermediary systems.</p>
						</Question>
						<Question question="What are the system requirements?">
							<p>WordPress 5.6+, WooCommerce 3.5+, and PHP 7.4+ (PHP 8.0+ recommended). HPOS is fully supported.</p>
						</Question>
						<Question question="How many sites can I use this on?">
							<p>Each purchase covers a single WooCommerce installation. Contact us for multi-site or agency pricing.</p>
						</Question>
						<Question question="Who makes this?">
							<p>Power Automate for WooCommerce is authored and maintained by <a href="https://www.reenhanced.com/" target="_blank" rel="noreferrer">Reenhanced</a>. For support, email <a href="mailto:support@reenhanced.com">support@reenhanced.com</a> or visit our <a href="https://reenhanced.com/contact" target="_blank" rel="noreferrer">contact form</a>.</p>
						</Question>
					</PivotItem>
				</Pivot>

				{/* ── Bottom CTA ── */}
				<div style={{
					backgroundColor: theme.palette.neutralLighterAlt,
					border: `1px solid ${theme.palette.neutralLight}`,
					borderRadius: 10,
					padding: '24px 28px',
					marginTop: 24,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					flexWrap: 'wrap' as const,
					gap: 16,
				}}>
					<div>
						<Text block style={{ ...(theme.fonts.large as React.CSSProperties), fontWeight: 700, marginBottom: 4 }}>
							Ready to automate your WooCommerce store?
						</Text>
						<Text block style={{ color: theme.palette.neutralSecondary }}>
							$299 / year &nbsp;&bull;&nbsp; Microsoft Certified &nbsp;&bull;&nbsp; No code required
						</Text>
					</div>
					<PrimaryButton
						text="Get Started at reenhanced.com"
						iconProps={{ iconName: 'ShoppingCart' }}
						onClick={() => { window.location.href = PRODUCT_URL; }}
					/>
				</div>

			</div>
		</>
	);
};

export default Woopower;
